import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import type { EdgeState, GridSnapshot, PacketState } from "@reagent/sim";
import { lookOf } from "../../reagents";
import { PALETTE } from "../palette";
import { gridToWorld } from "../iso/gridToWorld";
import { ReagentGlyph } from "./ReagentGlyph";

/**
 * The reagents currently inside the pipes.
 *
 * Until now these were drawn only by the `packet.moved` effect — a blob that
 * lived for 0.42s and then vanished. That meant the fullest board in the game
 * showed nothing at all between ticks: at "slow" a pipe holding four packets
 * was empty for two thirds of every tick, and pausing mid-run (the thing the
 * step button exists for) showed an empty rig. "Fourteen packets in flight"
 * was a number in the tuning console and nothing on the table.
 *
 * A packet's position is state, not an occurrence, so it belongs to the
 * snapshot and is drawn from the snapshot — which also makes the reagent
 * shapes worth having, since you can now look at a stopped board and read what
 * is in each line.
 *
 * The event-driven blob is gone; the smoothing here replaces it. Each glyph
 * eases from where it was to where the snapshot now says it is, so the motion
 * is the same squash-free glide, driven by state that is always correct rather
 * than by an event that might be missed.
 */

/** Seconds to cover one hop. Comfortably under the fastest tick rate (0.3s). */
const GLIDE_SECONDS = 0.22;
/**
 * Just clear of the tube, which tops out at 0.24 (PIPE_HEIGHT + PIPE_RADIUS).
 * Riding inside the pipe is prettier on the glass runs and invisible on every
 * alloy and PTFE one — and those are most of them, on most stages.
 */
const PACKET_HEIGHT = 0.29;

export function PacketsInFlight({ snapshot }: { snapshot: GridSnapshot }) {
  const edges = new Map(snapshot.edges.map((edge) => [edge.id, edge]));

  return (
    <group>
      {snapshot.packetsInFlight.map((packet) => {
        if (packet.location.kind !== "onEdge") return null;
        const edge = edges.get(packet.location.edgeId);
        if (!edge || edge.isBurst) return null;
        return <FlyingPacket key={packet.id} packet={packet} edge={edge} />;
      })}
    </group>
  );
}

/** World position of a packet `t` of the way along its pipe. */
function positionOn(edge: EdgeState, t: number): THREE.Vector3 {
  const last = edge.path.length - 1;
  if (last < 0) return new THREE.Vector3();
  // `t` comes off the snapshot as pathIndex/lastIndex, so it lands exactly on
  // a waypoint; interpolating between the two neighbours keeps this honest if
  // the engine ever reports a position between them.
  const scaled = Math.min(Math.max(t, 0), 1) * last;
  const lowIndex = Math.floor(scaled);
  const highIndex = Math.min(lowIndex + 1, last);
  const low = edge.path[lowIndex] ?? edge.path[0]!;
  const high = edge.path[highIndex] ?? low;

  const [lx, , lz] = gridToWorld(low, PACKET_HEIGHT);
  const [hx, , hz] = gridToWorld(high, PACKET_HEIGHT);
  const f = scaled - lowIndex;
  return new THREE.Vector3(lx + (hx - lx) * f, PACKET_HEIGHT, lz + (hz - lz) * f);
}

function FlyingPacket({ packet, edge }: { packet: PacketState; edge: EdgeState }) {
  const groupRef = useRef<THREE.Group>(null);
  const target = positionOn(edge, packet.location.kind === "onEdge" ? packet.location.t : 0);

  // Where it was last frame. A packet appearing for the first time starts at
  // its target rather than gliding in from the origin.
  const current = useRef<THREE.Vector3 | null>(null);

  useFrame((_, delta) => {
    const group = groupRef.current;
    if (!group) return;
    if (!current.current) current.current = target.clone();
    // Frame-rate independent easing: the same glide at 30fps and at 144.
    const k = 1 - Math.pow(0.001, delta / GLIDE_SECONDS);
    current.current.lerp(target, k);
    group.position.copy(current.current);
  });

  const look = lookOf(packet.composition);

  return (
    <group ref={groupRef} position={target}>
      <ReagentGlyph
        shape={look?.shape ?? "sphere"}
        color={look?.color ?? PALETTE.fluidSkyBlue}
        size={0.085}
      />
    </group>
  );
}
